/**
 * @typedef {Object} MealPlan
 * @property {string} baseCode - The base code of the meal plan, eg "BB" | "FB" | "HB" | "AI"...
 * @property {string} code - The code of the meal plan, eg "BB" | "FB" | "HB" | "AI"...
 * @property {string} name - The name of the meal plan, eg "Breakfast" | "Full Board" | "Half Board" | "All Inclusive"...
 */

/**
 * @typedef {Object} Accomodation
 * @property {string} startDate - The start date of the accomodation, eg "2023-09-02"
 * @property {string} endDate - The end date of the accomodation, eg "2023-09-09"
 * @property {string[][]} description - The description
 * @property {MealPlan} mealPlan - The meal plan
 * /

/**
 * @typedef {Object} TransferDetail
 * @property {string} code - The code of the transfer, eg "SPL"
 * @property {string} name - The name of the transfer, eg "Sea Plane" | "Speedboat"
 * @property {string[]} description - The description of the transfer
 */

/**
 * @typedef {Object} Transfer
 * @property {TransferDetail} arrival - The arrival transfer
 * @property {TransferDetail} departure - The departure transfer
 */

/**
 * @typedef {Object} FeeOrTax
 * @property {string} code - The code of the fee, eg "GTAX"
 * @property {string} name - The name of the fee, eg "Green Tax"
 * @property {string} type - The type of the fee "fixed_tax" | "ground_handling"
 * @property {string[]} description - The description of the fee
 */

/**
 * @typedef {Object} Price
 * @property {number} nett - The nett price
 * @property {number} gross - The gross price
 * @property {number} discount - The discount
 * @property {string} priceValidUntil - The date until the price is valid, eg "2021-12-31"
 * @property {string} currency - The currency of the price, eg "USD"
 */

/**
 * @typedef {Object} CancelationPolicy
 * @property {string} date - The date of the cancelation policy, eg "2021-12-31"
 * @property {number} amount - The amount of the cancelation policy, 50 or 100
 * @property {string} amountType - "percent" | "fixed"
 * @property {string} applyOn - "full_amount"
 * @property {string[]} description - The description of the cancelation policy
 * /

/**
 * @typedef {Object} HummingbirdDealInfo
 * @property {Accomodation} accommodation - The accommodation
 * @property {Transfer} transfer - The transfer
 * @property {string} availability - "free_sell" | "on_request"
 * @property {FeeOrTax[]} feesAndTaxes - The fees and taxes
 * /

/**
 * @typedef {Object} DealNote
 * @property {Object.<string, {accommodations: string[], transfers: string[], others: string[]}>} [general] - The general section
 * @property {Object.<string, {accommodations: string[], transfers: string[], others: string[]}>} [warning] - The warning section
 * @property {Object.<string, {accommodations: string[], transfers: string[], others: string[]}>} [term] - The term section
 * @property {Object.<string, {accommodations: string[], transfers: string[], others: string[]}>} [errata] - The errata section
 */

/**
 * @typedef {Object} HummingbirdDeal
 * @property {string} id - The id of the deal, eg "h5d3458036d47b:OV:BB:SPL:SPL"
 * @property {string} key - The key of the deal, "group-1:OV:h5d3458036d47b:OV:BB:SPL:SPL",
 *                         where "group-1:OV" is the group id
 * @property {HummingbirdDealInfo} info - The category of the deal
 * @property {price} price - The price
 * @property {CancelationPolicy[]} cancelationPolicies - The cancelation policies
 * @property {DealNote} notes - The notes
 */

/*
  For each transfer options, we need to get the price of the meal plan

  For example: transfer is SPL:SPL (arrival: sea plane, departure: sea plane)
  Meal plan can be: 
  - BB: $5555
  - HB: $6666
  - FB: $7777
  - AI: $8888

  We need to get the difference beween each, in relative to the cheapest meal plan
  - BB: 0
  - HB: 1111
  - FB: 2222
  - AI: 3333

  @param {HummingbirdDeal[]} hummingbirdDeals - Array of hummingbird deals
  @returns {Object} - a map of transfer <-> mealPlans and their prices

  Example:
  {
    "SPL:SPL": {
      "BB": [5555, 5555, 0, 0],
      "HB": [6666, 6666, 0, 1111],
    }
  }

  Explanation of the result:
  - SPL:SPL: transfer options
  - BB: meal plan
  - [5555, 5555, 0, 0]: [gross, nett, discount, difference from cheapest meal plan]
*/
export const breakdownMealPlanPrices = (hummingbirdDeals) => {
  const uniqueTransferOptions = new Set();

  for (const hummingbirdDeal of hummingbirdDeals) {
    const transferOptions = `${hummingbirdDeal.info.transfer.arrival.code}:${hummingbirdDeal.info.transfer.departure.code}`;
    uniqueTransferOptions.add(transferOptions);
  }

  const transferOptionsArray = [...uniqueTransferOptions];
  const result = {};

  for (const uniqueTransfer of transferOptionsArray) {
    const deals = hummingbirdDeals.filter(
      (deal) =>
        `${deal.info.transfer.arrival.code}:${deal.info.transfer.departure.code}` ===
        uniqueTransfer
    );

    result[uniqueTransfer] = {};
    for (const deal of deals) {
      result[uniqueTransfer][deal.info.accommodation.mealPlan.code] = [
        deal.price.gross,
        deal.price.nett,
        deal.price.discount,
      ];
    }
  }

  for (const transfer in result) {
    const lowestMealPlan = Math.min(
      ...Object.values(result[transfer]).map((item) => item[1]) // nett
    );

    for (const detail in result[transfer]) {
      const delta = result[transfer][detail][1] - lowestMealPlan;
      result[transfer][detail].push(delta);
    }
  }

  return result;
};

/*
  For each meal plan options, we need to get the price of the transfer

  @param {HummingbirdDeal[]} hummingbirdDeals - Array of hummingbird deals
  @returns {Object} - a map of mealPlan <-> transfers and their prices

  Example:
  {
    "BB": {
      "SPL:SPL": [5555, 5555, 0, 0],
      "VIPSPL:VIPSPL": [6666, 6666, 0, 1111],
    }
  }

  Explanation of the result:
  - SPL:SPL, VIPSPL:VIPSPL - transfer options
  - BB: meal plan
  - [5555, 5555, 0, 0]: [gross, nett, discount, difference from cheapest meal plan]
*/
export const breakdownTransferPrices = (hummingbirdDeals) => {
  const uniqueMealPlanOptions = new Set();

  for (const deal of hummingbirdDeals) {
    const mealPlanOptions = `${deal.info.accommodation.mealPlan.code}`;
    uniqueMealPlanOptions.add(mealPlanOptions);
  }

  const mealPlanOptionsArray = [...uniqueMealPlanOptions];
  const result = {};

  for (const uniqueMealPlanCode of mealPlanOptionsArray) {
    const deals = hummingbirdDeals.filter(
      (deal) => deal.info.accommodation.mealPlan.code === uniqueMealPlanCode
    );

    result[uniqueMealPlanCode] = {};
    for (const deal of deals) {
      const transferCode = `${deal.info.transfer.arrival.code}:${deal.info.transfer.departure.code}`;
      result[uniqueMealPlanCode][transferCode] = [
        deal.price.gross,
        deal.price.nett,
        deal.price.discount,
      ];
    }
  }

  for (const mealPlan in result) {
    const lowestTransfer = Math.min(
      ...Object.values(result[mealPlan]).map((item) => item[1]) // nett
    );

    for (const detail in result[mealPlan]) {
      const delta = result[mealPlan][detail][1] - lowestTransfer;
      result[mealPlan][detail].push(delta);
    }
  }

  return result;
};

export const guestsDisplay = (roomsFilter) => {
  let result = "";
  let adultsTotal = roomsFilter.reduce(
    (total, { adults }) => total + adults,
    0
  );
  let childTotal = roomsFilter.reduce(
    (total, { children }) => total + children.length,
    0
  );

  if (adultsTotal > 1) result = `${adultsTotal} adults`;
  else if (adultsTotal === 1) result = `${adultsTotal} adult`;

  if (childTotal > 0) result += ` • ${childTotal} children`;

  return result;
};

export const getTransfers = (selectedRoomDetails) => {
  let results = [];

  let guestsTotal = 0;
  for (const roomDetail of selectedRoomDetails) {
    guestsTotal += roomDetail.guests.adults + roomDetail.guests.children;
  }

  const roomDetail = selectedRoomDetails[0];
  const arrivalTransferName = roomDetail.deal.info.transfer.arrival.name;
  const departureTransferName = roomDetail.deal.info.transfer.departure.name;

  if (arrivalTransferName !== departureTransferName) {
    results.push({
      text: `${arrivalTransferName} - ${departureTransferName}`,
      qty: guestsTotal,
    });
  } else {
    results.push({
      text: arrivalTransferName,
      qty: guestsTotal,
    });
  }

  // then we combine result that has the same text and add up the qty
  const combinedResults = results.reduce((acc, curr) => {
    const existingResult = acc.find((r) => r.text === curr.text);

    if (existingResult) {
      existingResult.qty += curr.qty;
    } else {
      acc.push(curr);
    }

    return acc;
  }, []);

  return combinedResults;
};

export const setPositionSidebar = (isResultEmpty = false) => {
  const overviewHeight =
    document.getElementById("resort-overview").offsetHeight;
  const checkList = document.getElementById("resort-checklist");

  let spacing = overviewHeight;
  if (checkList) {
    spacing = overviewHeight - checkList.offsetHeight;
  }

  let boxPosition = spacing + 370;
  if (isResultEmpty) {
    boxPosition = spacing + 326;
  }

  document.getElementById("container-sidebar").style.top = boxPosition + "px";
  document.getElementById("container-sidebar").style.marginBottom =
    boxPosition + "px";
};

export const breakdownDealComponent = (dealId) => {
  const keys = dealId.split(":");
  const [
    hotelCode,
    roomCode,
    mealPlanCode,
    arrivalTransferCode,
    departureTransferCode,
  ] = keys;

  return {
    hotelCode,
    roomCode,
    mealPlanCode,
    arrivalTransferCode,
    departureTransferCode,
    transferCode: `${arrivalTransferCode}:${departureTransferCode}`,
  };
};
